import { useState, useEffect } from 'react';
import { SidebarNav } from './nav/SidebarNav';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AvisoPrimeirasAvaliacoes } from './primeiros/AvisoPrimeirasAvaliacoes';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { UserPlus, LogOut, Menu, X, Sun, Moon, Lock, Settings, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { PROFILE_LABELS } from '../lib/utils';
import NotificationBell from './NotificationBell';
import RealTimeAlerts from './RealTimeAlerts';

const Layout = ({ children }) => {
  const { user, logout, can } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('lp.nav.collapsed') === '1');
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 1024px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  const navCollapsed = collapsed && isDesktop;
  const toggleCollapsed = () => setCollapsed((c) => { localStorage.setItem('lp.nav.collapsed', c ? '0' : '1'); return !c; });

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-card border-r border-border
        transform transition-[transform,width] duration-200 ease-in-out flex flex-col
        lg:translate-x-0 ${collapsed ? 'lg:w-[4.5rem]' : 'lg:w-64'}
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo */}
        <div className={`h-16 flex items-center justify-between border-b border-border flex-shrink-0 px-4 ${collapsed ? 'lg:justify-center lg:px-0' : ''}`}>
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="sidebar-logo">
            <img src="/assets/logo-symbol.png" alt="LIFEPORT HOME" className="h-9 w-9 object-contain" />
            <span className={`font-bold text-lg font-['Manrope'] text-[hsl(var(--lifeport-red))] ${collapsed ? 'lg:hidden' : ''}`}>LIFEPORT HOME</span>
          </Link>
          <button className="lg:hidden p-2 hover:bg-muted rounded-md" onClick={() => setSidebarOpen(false)} aria-label="Fechar menu" data-testid="btn-close-sidebar">
            <X className="h-5 w-5" />
          </button>
          {!collapsed && (
            <button className="hidden lg:inline-flex p-2 hover:bg-muted rounded-md text-muted-foreground" onClick={toggleCollapsed} aria-label="Recolher menu" data-testid="btn-collapse-sidebar">
              <PanelLeftClose className="h-5 w-5" />
            </button>
          )}
        </div>
        {collapsed && (
          <button className="hidden lg:flex mx-auto mt-2 p-2 hover:bg-muted rounded-md text-muted-foreground" onClick={toggleCollapsed} aria-label="Expandir menu" data-testid="btn-expand-sidebar">
            <PanelLeftOpen className="h-5 w-5" />
          </button>
        )}

        <SidebarNav collapsed={navCollapsed} onNavigate={() => setSidebarOpen(false)} />

        {/* Quick Actions */}
        <div className={`border-t border-border flex-shrink-0 ${navCollapsed ? 'p-2' : 'p-4'}`}>
          {can('criar_pacientes') && (
          <Link to="/pacientes/novo" onClick={() => setSidebarOpen(false)}>
            <Button className={`w-full gap-2 ${collapsed ? 'lg:px-0' : ''}`} title="Novo Paciente" data-testid="btn-novo-paciente-sidebar">
              <UserPlus className="h-4 w-4" />
              <span className={collapsed ? 'lg:hidden' : ''}>Novo Paciente</span>
            </Button>
          </Link>
          )}
        </div>

        {/* User Info */}
        <div className={`border-t border-border bg-card flex-shrink-0 ${navCollapsed ? 'p-2' : 'p-4'}`}>
          <div className={`flex items-center gap-3 ${navCollapsed ? 'lg:justify-center' : ''}`}>
            <Avatar className="h-9 w-9">
              <AvatarFallback className="bg-accent text-accent-foreground text-sm">
                {user?.nome ? getInitials(user.nome) : 'U'}
              </AvatarFallback>
            </Avatar>
            <div className={`flex-1 min-w-0 ${collapsed ? 'lg:hidden' : ''}`}>
              <p className="text-sm font-medium truncate">{user?.nome}</p>
              <p className="text-xs text-muted-foreground">{PROFILE_LABELS[user?.perfil]}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className={`transition-[padding] duration-200 ${collapsed ? 'lg:pl-[4.5rem]' : 'lg:pl-64'}`}>
        {/* Top header */}
        <header className="h-16 bg-card border-b border-border sticky top-0 z-30">
          <div className="h-full px-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button 
                className="lg:hidden p-2 hover:bg-muted rounded-md"
                onClick={() => setSidebarOpen(true)}
                data-testid="btn-mobile-menu"
              >
                <Menu className="h-5 w-5" />
              </button>

              {/* Logo + Title in header */}
              <Link to="/dashboard" className="flex items-center gap-2">
                <img 
                  src="/assets/lifeport-simbolo.png" 
                  alt="Logo Lifeport" 
                  className="h-8 w-8 object-contain"
                />
                <div className="hidden min-[420px]:flex flex-col">
                  <span className="font-bold text-base font-['Manrope'] text-[hsl(var(--lifeport-red))] whitespace-nowrap">Lifeport Home</span>
                  <span className="text-[10px] text-muted-foreground leading-none">Saúde no Lar</span>
                </div>
              </Link>
            </div>

            <div className="flex-1" />

            <div className="flex items-center gap-2">
              {/* Real-time Alerts */}
              <RealTimeAlerts />
              
              {/* Notifications */}
              <NotificationBell />
              
              {/* Theme toggle */}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                data-testid="btn-theme-toggle"
              >
                {theme === 'dark' ? (
                  <Sun className="h-5 w-5" />
                ) : (
                  <Moon className="h-5 w-5" />
                )}
              </Button>

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2" data-testid="btn-user-menu">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className="bg-accent text-accent-foreground text-xs">
                        {user?.nome ? getInitials(user.nome) : 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden md:block text-sm">{user?.nome}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium">{user?.nome}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/configuracoes')} data-testid="btn-configuracoes">
                    <Settings className="h-4 w-4 mr-2" />
                    Configurações
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/alterar-senha')} data-testid="btn-alterar-senha">
                    <Lock className="h-4 w-4 mr-2" />
                    Alterar Senha
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="btn-logout">
                    <LogOut className="h-4 w-4 mr-2" />
                    Sair
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        <AvisoPrimeirasAvaliacoes />
        {/* Page content */}
        <main className="lp-main p-3 sm:p-6 page-enter">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
